import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, TextField } from '@mui/material';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import {
  CHANGE_TYPE_LABELS,
  CONNECTION_TYPES,
  SIM_CHANGE_PROCESS_TYPES,
  simChangeSchema,
} from '@app/shared';
import type { ConnectionType, CustomerDto, SimChangeInput, SimChangeResultDto } from '@app/shared';
import { LoginIdSuggest } from '../../components/LoginIdSuggest';
import { useNotify } from '../../components/NotifyProvider';
import { ConfirmDialog } from '../../components/dialogs';
import { SelectInput, TextInput, applyServerErrors } from '../../components/form';
import { errorMessage } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import { useChangeReasonOptions } from '../inventory/api';
import { withRequiredMessages } from '../inventory/formUtils';
import { statusLabel } from '../inventory/labels';
import { useChangeSim } from './api';

interface SimForm {
  newSim: string;
  reasonId: string | null;
  connectionType: ConnectionType | '';
  returnedTo: string;
  badReason: string;
}
const EMPTY: SimForm = {
  newSim: '',
  reasonId: null,
  connectionType: '',
  returnedTo: '',
  badReason: '',
};

/** What became of the SIM that was taken back, in the words the inventory screens use. */
const describeResult = (result: SimChangeResultDto): string =>
  `The line now uses SIM ${result.customer.line.sim}. ` +
  (result.oldSimStatus
    ? `The old SIM ${result.oldSim} is now ${statusLabel(result.oldSimStatus)} in inventory.`
    : `The old SIM ${result.oldSim} was not an inventory unit.`);

/**
 * ESN/SIM Change (PC18): moves the line to another SIM from the user's stock. Labels and
 * options are the reference's (appendix 13.5). Its "Model" is not built: a SIM of the stock
 * already has its model. "Who got it back (Employee Login ID)" is behind its release switch;
 * the old SIM then goes to that user's stock (assumption).
 */
export function SimChangeForm({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const reasons = useChangeReasonOptions();
  const changeSim = useChangeSim(customer.id);
  const [pending, setPending] = useState<SimChangeInput | null>(null);
  const [result, setResult] = useState<SimChangeResultDto | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const featureOn = useFeatureReleased();
  const { control, handleSubmit, reset, setError } = useForm<SimForm>({
    defaultValues: EMPTY,
    resolver: withRequiredMessages<SimForm>(
      zodResolver(simChangeSchema) as unknown as Resolver<SimForm>,
      { newSim: 'Enter the New ESN/SIM', reasonId: 'Select Reason', connectionType: 'Select Type' },
    ),
  });
  const chosenReason = useWatch({ control, name: 'reasonId' });

  const { line, status } = customer;
  if ((status !== 'Active' && status !== 'Suspended') || !line.sim || !line.mdn)
    return (
      <>
        {result ? <Alert severity="success">{describeResult(result)}</Alert> : null}
        <Alert severity="info" sx={{ mt: result ? 1.5 : 0 }}>
          The SIM can be changed on an Active or Suspended account that has a SIM and a telephone
          number.
        </Alert>
      </>
    );

  // "Reason for Bad ESN/SIM" is asked when the reason marks the old SIM Bad (switch bad-reasons).
  const askBad =
    featureOn('bad-reasons') &&
    (reasons.data ?? []).find((r) => r.id === chosenReason)?.reasonStatus === 'Bad';

  // Only reasons meant for a SIM change ("CHANGE ESN" or "BOTH").
  const reasonOptions = (reasons.data ?? [])
    .filter((r) => (SIM_CHANGE_PROCESS_TYPES as readonly string[]).includes(r.processType))
    .map((r) => ({ value: r.id, label: r.name }));

  const ask = handleSubmit((values) => {
    if (askBad && !values.badReason.trim())
      return setError('badReason', { message: 'Enter the Reason for Bad ESN/SIM' });
    setFailure(null);
    setResult(null);
    setPending({ ...values, badReason: askBad ? values.badReason : '' } as SimChangeInput);
  });

  const confirm = async () => {
    if (!pending) return;
    try {
      const done = await changeSim.mutateAsync(pending);
      setResult(done);
      notify.success('SIM changed.');
      reset(EMPTY);
    } catch (err) {
      // A refusal by the carrier (502) or a conflict has no field; it is shown on the page.
      if (!applyServerErrors(err, setError)) setFailure(errorMessage(err));
    } finally {
      setPending(null);
    }
  };

  return (
    <Box component="form" noValidate onSubmit={(e) => void ask(e)} aria-label="ESN/SIM Change">
      {result ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResult(null)}>
          {describeResult(result)}
        </Alert>
      ) : null}
      {failure ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFailure(null)}>
          {failure}
        </Alert>
      ) : null}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField label="MDN" value={line.mdn} slotProps={{ input: { readOnly: true } }} />
        <TextField label="Old ESN/SIM" value={line.sim} slotProps={{ input: { readOnly: true } }} />
        <TextInput
          control={control}
          name="newSim"
          label="New ESN/SIM"
          required
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 22, inputMode: 'numeric' } }}
        />
        <SelectInput
          control={control}
          name="reasonId"
          label="Reason for Change ESN/SIM"
          required
          options={reasonOptions}
          helperText={reasons.isError ? 'Could not load the reasons' : undefined}
        />
        <SelectInput
          control={control}
          name="connectionType"
          label="Type of Change"
          required
          options={CONNECTION_TYPES.map((t) => ({ value: t, label: CHANGE_TYPE_LABELS[t] }))}
        />
        {askBad ? (
          <TextInput
            control={control}
            name="badReason"
            label="Reason for Bad ESN/SIM"
            required
            placeholder="What is wrong with the old SIM"
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
        ) : null}
        {featureOn('who-got-it-back') ? (
          <LoginIdSuggest
            control={control}
            name="returnedTo"
            label="Who got it back (Employee Login ID)"
          />
        ) : null}
      </Box>
      <Button type="submit" variant="contained" disabled={changeSim.isPending} sx={{ mt: 2 }}>
        Submit
      </Button>

      <ConfirmDialog
        open={Boolean(pending)}
        title="ESN/SIM Change"
        message={
          pending
            ? `Move telephone number ${line.mdn} from SIM ${line.sim} to SIM ${pending.newSim} (${pending.connectionType})?`
            : ''
        }
        confirmLabel="Change ESN/SIM"
        busy={changeSim.isPending}
        onConfirm={() => void confirm()}
        onClose={() => setPending(null)}
      />
    </Box>
  );
}
